/**
 * What the guest did inside the page, sent in batches and never in the way.
 *
 * The server sees most of the funnel already — it serves the trip, the store, the
 * pre-check-in. What it cannot see is what happens after the page has its data:
 * which block of the guide was expanded, whether the access code was ever revealed,
 * whether the guide was opened at all. That is what this reports.
 *
 * Three rules it keeps, in order of importance:
 *
 *   1. It never breaks the page. Every entry point is wrapped; a missing API, a
 *      blocked request, a browser without sendBeacon — all of it ends in a shrug.
 *   2. It never chats. Events are buffered and folded (the same block opened five
 *      times is one entry with count 5), then sent at most once every FLUSH_MS, so a
 *      restless finger costs one request rather than thirty.
 *   3. It never loses the last one. A guest closing the tab is the most interesting
 *      moment of all, so the buffer is flushed on the way out with sendBeacon, which
 *      survives the page being torn down; a normal request would not.
 */
import axios from 'axios'

const FLUSH_MS = 5000
// A page that produces more than this in one window is either broken or being
// played with; either way the server would cap it at 50.
const MAX_BUFFER = 40

const buffer = new Map()
let timer = null
let reservationCode = null
let listening = false

/**
 * The stay everything is reported against. Set when the trip page opens.
 *
 * Anything still buffered belongs to the stay it was collected on, so switching
 * stays sends it first. Without that, a guest moving from one trip to the next has
 * the first trip's blocks reported against the second one — the buffer is flushed
 * against whatever code is current when the timer finally fires.
 */
export function setActivityReservation(code) {
  const next = code || null
  if (next !== reservationCode && buffer.size > 0) flush(false)

  reservationCode = next
}

function key(event, subject) {
  return `${event}|${subject == null ? '' : subject}`
}

function flush(useBeacon) {
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  if (!reservationCode || buffer.size === 0) return

  const events = Array.from(buffer.values())
  buffer.clear()

  const url = `/api/tourist/reservations/${encodeURIComponent(
    reservationCode
  )}/activity`

  try {
    // On the way out the page may be gone before a normal request is sent, and an
    // async one is cancelled; sendBeacon hands the payload to the browser to deliver
    // on its own. Same origin, so the session cookie goes with it.
    if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify({ events })], {
        type: 'application/json',
      })
      navigator.sendBeacon(url, blob)
      return
    }
    axios.post(url, { events }).catch(() => {})
  } catch (e) {
    // Reporting is not worth a single broken screen.
  }
}

function startListening() {
  if (listening || typeof document === 'undefined') return
  listening = true

  // visibilitychange is the one event that fires reliably on mobile, where a tab is
  // never "closed" so much as backgrounded and later killed. pagehide covers the
  // desktop case and the bfcache.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true)
  })
  window.addEventListener('pagehide', () => flush(true))
}

/**
 * Report something the guest did. Fire and forget.
 *
 * @param {string} event one of the events the server accepts; anything else is dropped there
 * @param {string|number} [subject] which part of the screen — a guide block, an item id
 */
export function trackGuestActivity(event, subject) {
  try {
    if (!event || !reservationCode) return

    startListening()

    const id = key(event, subject)
    const existing = buffer.get(id)
    if (existing) {
      existing.count += 1
    } else if (buffer.size < MAX_BUFFER) {
      buffer.set(id, {
        event,
        subject: subject == null ? null : String(subject),
        at: new Date().toISOString(),
        count: 1,
      })
    }

    if (!timer) timer = setTimeout(() => flush(false), FLUSH_MS)
  } catch (e) {
    // As above: a statistic must never be the reason something on this page fails.
  }
}

export default { trackGuestActivity, setActivityReservation }
